import { t } from "@lingui/macro";
import {
  type GetBudgetExceededQuery,
  ProjectOrderByColumnEnum,
  ProjectStatusEnum,
  ProjectsWhereColumn,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { can } from "@src/utils/components/permissions";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";
import mapToOptions from "../../../../utils/map-to-options";
import { getBillingCategoryOptions } from "../list/Store";
import { ProjectModel } from "./models";

export enum Quickfilter {
  AllActive = "active",
  MyActive = "my_active",
  Finished = "finished",
  Archived = "archived",
}
export class StoreBudgetExceeded {
  @observable searchTerm = "";
  @observable.ref projects: ProjectModel[] = [];

  pagination = new PaginationState("projectsBudgetExceeded");

  orderBy = new OrderBy<ProjectOrderByColumnEnum>([
    { column: ProjectOrderByColumnEnum.ProjectCode, order: SortOrder.Asc },
  ]);

  where = new Filters<ProjectsWhereColumn>(
    [
      new Filter({
        column: ProjectsWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.OtherProjectManagerId,
        operator: SqlOperator.In,
        title: t`Other Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        column: ProjectsWhereColumn.QuickFilter,
        operator: SqlOperator.Eq,
        title: t`Projects`,
        hidden: true,
        options: [
          {
            label: t`My active`,
            value: Quickfilter.MyActive,
          },
          {
            label: t`All active`,
            value: Quickfilter.AllActive,
          },
          {
            label: t`Finished`,
            value: ProjectStatusEnum.Finished,
          },
          {
            label: t`Archived`,
            value: ProjectStatusEnum.Archived,
          },
        ],
      }),
      new Filter({
        column: ProjectsWhereColumn.Billable,
        operator: SqlOperator.Eq,
        title: t`Billing category`,
        options: getBillingCategoryOptions(),
      }),
      new Filter({
        column: ProjectsWhereColumn.IsTemplate,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
        value: "false",
      }),
    ],
    {
      onChange: this.pagination.resetPage,
      tableKey: "budget-exceeded",
      orderBy: this.orderBy,
    },
  );

  constructor() {
    makeObservable(this);
    this.toggleSubRows = this.toggleSubRows.bind(this);
    if (can("project_read_own") && !can("project_read_all")) {
      this.activeQuickFilter?.setValue(Quickfilter.MyActive);
    } else {
      this.activeQuickFilter?.setValue(Quickfilter.AllActive);
    }
  }

  @action setSearchTerm = (val: string) => {
    this.searchTerm = val;
  };

  toggleSubRows() {
    const state = !this.projects.every((p) => p.isOpen);
    this.projects = this.projects.map((p) => {
      p.isOpen = state;
      return p;
    });
  }

  fromGetBudgetExceededQuery(data: GetBudgetExceededQuery) {
    this.projects = ProjectModel.fromGetBudgetExceededQuery(data.projects);
    if (data.projects) {
      this.pagination.setFromPaginatorInfo(data.projects.paginatorInfo);
    }

    const pmOptions = mapToOptions.projectManagers.toOptions(
      data.projectManagerSimpleMap,
    );
    this.where.filtersByColumn
      .get(ProjectsWhereColumn.ProjectManagerId)
      ?.setOptions(pmOptions);
    this.where.filtersByColumn
      .get(ProjectsWhereColumn.OtherProjectManagerId)
      ?.setOptions(pmOptions);

    this.where.filtersByColumn.get(ProjectsWhereColumn.BrandId)?.setOptions(
      data.clientsSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
        options: i?.brands.map((brand) => {
          return {
            label: brand.name,
            value: brand.id,
          };
        })!,
      })),
    );

    this.where.filtersByColumn.get(ProjectsWhereColumn.TeamId)?.setOptions(
      data.teamSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );
  }

  @computed get activeQuickFilter() {
    return this.where.filtersByColumn.get(ProjectsWhereColumn.QuickFilter);
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get queryParams() {
    const variables = { ...commonQueryVariables(this) };
    variables.filters = {
      ...variables.filters,
      ...{ hasBudgetExceeded: true },
    };
    return variables;
  }

  static create() {
    return new StoreBudgetExceeded();
  }
}
